import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { departmentsApi, CreateDepartmentData } from '../../api/departmentsApi';
import { Department } from '../../types';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Card } from '../../components/common/Card';
import { Icons } from '../../components/common/Icons';
import './Departments.css';

export function Departments() {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<CreateDepartmentData>({
    name: '',
  });

  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await departmentsApi.getDepartments();
      setDepartments(res.departments);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const handleCreateDepartment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await departmentsApi.createDepartment(formData);
      setDepartments([...departments, res.department]);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create department');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDepartment = async (deptId: number) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;
    try {
      setLoading(true);
      await departmentsApi.deleteDepartment(deptId);
      setDepartments(departments.filter(d => d.id !== deptId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete department');
    } finally {
      setLoading(false);
    }
  };

  const handleDepartmentClick = (dept: Department) => {
    navigate(`/employees?department_id=${dept.id}`);
  };

  const resetForm = () => {
    setShowModal(false);
    setFormData({ name: '' });
  };

  return (
    <div className="departments-page">
      <div className="departments-header">
        <div className="departments-title">
          <h1>Department Management</h1>
          <p>Manage organizational departments</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Icons.Plus /> Add Department
        </Button>
      </div>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <div className="departments-grid">
        {departments.map((dept) => (
          <Card 
            key={dept.id} 
            className="department-card clickable"
            onClick={() => handleDepartmentClick(dept)}
          >
            <div className="department-card-content">
              <div className="department-icon">
                <Icons.Building />
              </div>
              <div className="department-info">
                <h3>{dept.name}</h3>
                <div className="department-stats">
                  <span className="stat">
                    <Icons.Users /> {dept.user_count} users
                  </span>
                  <span className={`status ${dept.is_active ? 'active' : 'inactive'}`}>
                    {dept.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              {dept.user_count === 0 && (
                <div className="department-actions">
                  <button 
                    className="action-btn delete" 
                    onClick={(e) => { e.stopPropagation(); handleDeleteDepartment(dept.id); }}
                  >
                    <Icons.Trash />
                  </button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {departments.length === 0 && !loading && (
        <Card className="empty-state">
          <div className="empty-state-content">
            <Icons.Building />
            <h3>No departments yet</h3>
            <p>Create your first department to get started</p>
            <Button onClick={() => setShowModal(true)}>
              <Icons.Plus /> Add Department
            </Button>
          </div>
        </Card>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Department</h2>
              <button className="modal-close" onClick={resetForm}>&times;</button>
            </div>
            <form onSubmit={handleCreateDepartment}>
              <div className="modal-body">
                <Input
                  label="Department Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ name: e.target.value })}
                  placeholder="Enter department name"
                  required
                />
              </div>
              <div className="modal-footer">
                <Button type="button" variant="ghost" onClick={resetForm}>Cancel</Button>
                <Button type="submit" isLoading={loading}>
                  Create Department
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Departments;
